// @tennis-matches/services/governance
// Einheitliche Ladung von Bindings, Entitlements und role_app_access-Zeilen für den aktuellen Nutzer.

import { loadMyRoleBindingsRemote, invalidateMyRoleBindingsCache } from "./role-bindings.service.js";
import { loadMyAppEntitlementsRemote } from "./app-entitlements.service.js";
import { loadRoleAppAccessRemote, shouldLoadGlobalRoleAppAccessRemote } from "./role-app-access.service.js";
import { relinkGovernanceIdentityForSessionEmailRemote } from "./governance-identity-relink.service.js";
import { invalidateGovernancePendingWaitCache } from "./pending-wait.service.js";
import { setAuthDebugStep } from "../auth/auth-debug-state.js";
import { getSupabaseClient } from "../supabase/index.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __govBootstrapDevLog(tag, extra = {}) {
  try {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
    if (typeof console === "undefined" || !console.debug) return;
    const wallMs = typeof performance !== "undefined" ? Math.round(performance.now()) : 0;
    console.debug(tag, { ...extra, wallMs });
  } catch (_) {}
}

function __govNowMs() {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

const GOV_CACHE_KEY = "tm.governance.bundle.cache";

/** Parallele Aufrufer (Auth-Events, Strict Mode) teilen eine laufende Bundle-Ladung. */
let __govBundleInflight = null;
let __govBundleInflightUserId = "";

function __writeGovCache(bundle) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(GOV_CACHE_KEY, JSON.stringify({ ts: Date.now(), ...bundle }));
  } catch (_) {}
}

/**
 * @param {string} [expectedUserId] – wenn gesetzt, Cache nur für dieselbe Session-User-ID
 */
export function readGovernanceBundleCache(expectedUserId = "") {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(GOV_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ok) return null;
    const age = Date.now() - (parsed.ts || 0);
    if (age > 300000) return null;
    const expected = __asStr(expectedUserId);
    if (!expected) return null;
    if (__asStr(parsed.userId) !== expected) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/** Nach Matrix-Änderungen: Shell/Hub lädt Governance-Bundle neu. */
export function invalidateGovernanceBundleCache() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(GOV_CACHE_KEY);
    }
  } catch (_) {}
  __govBundleInflight = null;
  __govBundleInflightUserId = "";
  try {
    invalidateGovernancePendingWaitCache();
  } catch (_) {}
  try {
    invalidateMyRoleBindingsCache();
  } catch (_) {}
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("tm:governance-bundle-refresh"));
    }
  } catch (_) {}
}

/**
 * Lädt Governance-Daten für den eingeloggten Nutzer (gleiche Quellen wie Web-Shell).
 * @param {{ onBindingsReady?: (partial: { ok: boolean, missing?: boolean, bindings: any[], entitlements: any[], roleAppAccessRows: any[], identityRelink?: unknown }) => void }} [options]
 * @returns {{ ok: boolean, missing?: boolean, bindings: any[], entitlements: any[], roleAppAccessRows: any[], identityRelink?: unknown }}
 */
async function __loadMyGovernanceAccessBundleRemoteImpl(options = {}) {
  const { onBindingsReady } = options;
  const t0 = __govNowMs();
  setAuthDebugStep("governance:start");

  __govBootstrapDevLog("governance: relink + bindings parallel", { phase: "start" });
  setAuthDebugStep("roles:start");
  const supabase = await getSupabaseClient();
  const [relinkResult, r] = await Promise.all([
    relinkGovernanceIdentityForSessionEmailRemote().catch((e) => {
      __govBootstrapDevLog("governance: relink failed (non-blocking)", { err: String(e?.message || e) });
      return { ok: false, reason: "exception" };
    }),
    loadMyRoleBindingsRemote(),
  ]);
  const identityRelink = relinkResult ?? null;
  const bindings = r?.ok && Array.isArray(r.bindings) ? r.bindings : [];
  const missing = Boolean(r?.missing);
  __govBootstrapDevLog("governance: relink + bindings resolved", {
    ms: Math.round((__govNowMs() - t0) * 10) / 10,
    count: bindings.length,
    missing,
    ok: r?.ok,
    reason: r?.reason || null,
    roles: bindings.map((b) => `${__asStr(b?.scope)}:${__asStr(b?.scope_id)}:${__asStr(b?.role)}`),
  });
  if (missing && bindings.length === 0) {
    __govBootstrapDevLog("governance: WARNING — role_bindings returned missing=true with 0 bindings; user may see 'no apps'");
  }
  setAuthDebugStep("roles:success");

  const { data: authData } = supabase ? await supabase.auth.getUser() : { data: null };
  const authUser = authData?.user ?? null;
  const authUserId = __asStr(authUser?.id);

  const prevCache = readGovernanceBundleCache(authUserId);
  const bindingsPartial = {
    ok: true,
    missing,
    bindings,
    entitlements: Array.isArray(prevCache?.entitlements) ? prevCache.entitlements : [],
    roleAppAccessRows: Array.isArray(prevCache?.roleAppAccessRows) ? prevCache.roleAppAccessRows : [],
    identityRelink,
    userId: authUserId,
  };
  __writeGovCache(bindingsPartial);
  if (typeof onBindingsReady === "function") {
    try {
      onBindingsReady(bindingsPartial);
    } catch (_) {}
  }

  const tenantScopeIds = Array.from(
    new Set(
      bindings
        .filter((b) => __asStr(b?.scope).toLowerCase() === "tenant")
        .map((b) => __asStr(b?.scope_id))
        .filter(Boolean)
    )
  );

  const tParallel0 = __govNowMs();
  setAuthDebugStep("entitlements+roleAppAccess:start");

  const needsGlobalRoleAppAccess = shouldLoadGlobalRoleAppAccessRemote(bindings, authUser);
  const globalRaPromise = needsGlobalRoleAppAccess
    ? loadRoleAppAccessRemote("global", "global")
    : Promise.resolve({ ok: true, rows: [], skippedScope: true });

  const [entitlementsResult, globalRaRes, ...scopeResults] = await Promise.all([
    loadMyAppEntitlementsRemote(),
    globalRaPromise,
    ...tenantScopeIds.map((sid) => loadRoleAppAccessRemote("tenant", sid).then((rr) => ({ sid, rr }))),
  ]);

  const entitlements = entitlementsResult?.ok && Array.isArray(entitlementsResult.entitlements) ? entitlementsResult.entitlements : [];
  __govBootstrapDevLog("governance: app entitlements loaded", {
    ms: Math.round((__govNowMs() - tParallel0) * 10) / 10,
    count: entitlements.length,
  });
  setAuthDebugStep("entitlements:success");

  const rows = [];
  if (globalRaRes?.ok) {
    for (const row of globalRaRes.rows || []) {
      rows.push({ scope: "global", scope_id: "global", ...row });
    }
  }
  __govBootstrapDevLog("governance: role_app_access scope", {
    scopeId: "global",
    ok: !!globalRaRes?.ok,
    rowCount: Array.isArray(globalRaRes?.rows) ? globalRaRes.rows.length : 0,
  });
  for (const { sid, rr } of scopeResults) {
    if (rr?.ok) {
      for (const row of rr.rows || []) rows.push({ scope: "tenant", scope_id: sid, ...row });
    }
    __govBootstrapDevLog("governance: role_app_access scope", {
      scopeId: sid,
      ok: !!rr?.ok,
      rowCount: Array.isArray(rr?.rows) ? rr.rows.length : 0,
    });
  }
  __govBootstrapDevLog("governance: entitlements+roleAppAccess parallel complete", {
    ms: Math.round((__govNowMs() - tParallel0) * 10) / 10,
    tenantScopeCount: tenantScopeIds.length,
    totalRows: rows.length,
  });

  __govBootstrapDevLog("governance: bundle complete", {
    ms: Math.round((__govNowMs() - t0) * 10) / 10,
    bindingsCount: bindings.length,
    entitlementsCount: entitlements.length,
    roleAppAccessRowsCount: rows.length,
    missing,
  });
  const result = {
    ok: true,
    missing,
    bindings,
    entitlements,
    roleAppAccessRows: rows,
    identityRelink,
    userId: authUserId,
  };
  __writeGovCache(result);
  return result;
}

export async function loadMyGovernanceAccessBundleRemote(options = {}) {
  let uid = "";
  try {
    const supabase = await getSupabaseClient();
    if (supabase) {
      const { data } = await supabase.auth.getUser();
      uid = __asStr(data?.user?.id);
    }
  } catch (_) {}

  if (__govBundleInflight && __govBundleInflightUserId === uid) {
    return __govBundleInflight;
  }

  const promise = __loadMyGovernanceAccessBundleRemoteImpl(options);
  __govBundleInflight = promise;
  __govBundleInflightUserId = uid;
  try {
    return await promise;
  } finally {
    if (__govBundleInflight === promise) {
      __govBundleInflight = null;
      __govBundleInflightUserId = "";
    }
  }
}
